import { type AnnouncementContentVerticalAlign } from '@components/announcement/AnnouncementContentVerticalAlign';
import { useAnnouncementBackgroundImageEditor } from '@components/announcement/hooks/useAnnouncementBackgroundImageEditor.hooks';
import { useAnnouncementContent } from '@components/announcement/hooks/useAnnouncementContent.hooks';
import { useAnnouncementFonts } from '@components/announcement/hooks/useAnnouncementFonts.hooks';
import { useAnnouncementItemData } from '@components/announcement/hooks/useAnnouncementItemData.hooks';
import { useAnnouncementItemEditorState } from '@components/announcement/hooks/useAnnouncementItemEditorState.hooks';
import { useAnnouncementPreview } from '@components/announcement/hooks/useAnnouncementPreview.hooks';
import { useAnnouncementRange } from '@components/announcement/hooks/useAnnouncementRange.hooks';
import { useAnnouncementTextToSpeech } from '@components/announcement/hooks/useAnnouncementTextToSpeech.hooks';
import { useSaveAnnouncementItem } from '@components/announcement/hooks/useSaveAnnouncementItem.hooks';
import { type AnnouncementItemEditorState } from '@components/announcement/util/announcementItemEditorState.util';
import { toPlainText } from '@components/announcement/util/announcementRichTextContent.util';
import { AnchorXDTO } from '@model/dynamicTimeTable/AnchorXDTO';
import { AnchorYDTO } from '@model/dynamicTimeTable/AnchorYDTO';
import { ColorDTO } from '@model/dynamicTimeTable/ColorDTO';
import { type JSONContent } from '@tiptap/core';
import { type Editor } from '@tiptap/react';
import { toSingleLineText } from '@util/string.util';
import { RichTextEditorProvider } from 'mui-tiptap';
import React, { createContext, useContext, useState } from 'react';
import { hasPermission } from '@/hooks';
import { AnnouncementDTO, AnnouncementRangeDTO, ImageResizeType } from '@/model';
import { PermissionType } from '@/type';

export const AnnouncementEditorProvider = ({
  announcementId,
  itemId,
  children,
}: AnnouncementEditorProviderProps) => {
  const { fontIdByName, fontNameById } = useAnnouncementFonts();

  const { fetchedAnnouncement, existingItem } = useAnnouncementItemData({
    announcementId,
    itemId,
  });

  // Based on the persisted flag, not the editor state — only a rich text
  // writer can toggle it, and they are never locked out anyway.
  const isReadOnly =
    !!existingItem?.template &&
    !hasPermission([PermissionType.PERM_ANNOUNCEMENT_RICH_TEXT_WRITE]);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const onDirty = () => setHasUnsavedChanges(true);

  const [nameError, setNameError] = useState(false);
  const [contentError, setContentError] = useState(false);

  const rangeState = useAnnouncementRange({ existingItem, onDirty });
  const { baseItem, onAnnouncementRangeModalOpen, onItemSaved } = rangeState;

  const editorStateBundle = useAnnouncementItemEditorState({
    existingItem,
    fetchedAnnouncement,
    onDirty,
  });
  const { editorState, updateEditorState } = editorStateBundle;

  const backgroundImageEditorState = useAnnouncementBackgroundImageEditor({
    editorState,
    updateEditorState,
  });

  const { content, editor, richTextContent } = useAnnouncementContent({
    existingItem,
    fontNameById,
    fontIdByName,
    editable: !isReadOnly,
    onDirty,
  });

  const textToSpeechState = useAnnouncementTextToSpeech({ existingItem, onDirty });
  const { textToSpeech, onTextToSpeechChange } = textToSpeechState;

  const onCopyTtsContentFromRichTextEditor = () => {
    onTextToSpeechChange(toSingleLineText(toPlainText(content)));
  };

  const previewState = useAnnouncementPreview({
    editorState,
    richTextContent,
  });

  const saveState = useSaveAnnouncementItem({
    announcementId,
    itemId,
    fetchedAnnouncement,
    baseItem,
    editorState,
    content,
    richTextContent,
    textToSpeech,
    onMissingRange: onAnnouncementRangeModalOpen,
    onMissingName: () => setNameError(true),
    onMissingContent: () => setContentError(true),
    onSaved: () => setHasUnsavedChanges(false),
    onItemSaved,
  });

  const contextValue: AnnouncementEditorContextValue = {
    editor,
    content,
    announcementId,
    itemId,
    fetchedAnnouncement,
    ...editorStateBundle,
    ...backgroundImageEditorState,
    ...textToSpeechState,
    onCopyTtsContentFromRichTextEditor,
    ...previewState,
    ...saveState,
    hasUnsavedChanges,
    nameError,
    contentError,
    isReadOnly,
    ...rangeState,
    announcementName: fetchedAnnouncement?.name,
    announcementWidth: fetchedAnnouncement?.width,
    announcementHeight: fetchedAnnouncement?.height,
  };

  return (
    <AnnouncementEditorContext.Provider value={contextValue}>
      <RichTextEditorProvider editor={editor}>{children}</RichTextEditorProvider>
    </AnnouncementEditorContext.Provider>
  );
};

interface AnnouncementEditorContextValue {
  announcementId: number;
  // undefined means a not-yet-saved item — see NEW_ANNOUNCEMENT_ITEM_ID.
  itemId: number | undefined;
  fetchedAnnouncement?: AnnouncementDTO;

  editor: Editor | null;
  content: JSONContent;
  textToSpeech: string;
  editorState: AnnouncementItemEditorState;
  contentBoxRef: React.RefObject<HTMLDivElement>;
  backgroundImage?: string;
  backgroundImageName?: string;
  announcementName?: string;
  announcementWidth?: number;
  announcementHeight?: number;

  blackAndWhitePreview: boolean;
  automaticPreview: boolean;
  previewImage?: string;
  isPreviewPending: boolean;
  isPreviewError: boolean;

  imageGalleryModalOpen: boolean;
  announcementRangeModalOpen: boolean;
  announcementRange?: AnnouncementRangeDTO;

  isSaving: boolean;
  hasUnsavedChanges: boolean;
  nameError: boolean;
  contentError: boolean;
  isReadOnly: boolean;

  onNameChange: (value: string) => void;
  onTemplateChange: (value: boolean) => void;
  onXChange: (value: number) => void;
  onYChange: (value: number) => void;
  onTextToSpeechChange: (value: string) => void;
  onCopyTtsContentFromRichTextEditor: () => void;
  onVerticalAlign: (align: AnnouncementContentVerticalAlign) => void;
  onImageResizeTypeChange: (value: ImageResizeType) => void;
  onBackgroundHorizontalAlignChange: (value: AnchorXDTO) => void;
  onBackgroundVerticalAlignChange: (value: AnchorYDTO) => void;
  onBackgroundColorChange: (value: ColorDTO) => void;
  onUploadBackgroundImage: (base64File: string, fileName: string) => void;
  onSelectBackgroundImage: () => void;
  onRemoveBackgroundImage: () => void;

  onBlackAndWhitePreviewChange: (value: boolean) => void;
  onAutomaticPreviewChange: (value: boolean) => void;
  onGeneratePreview: () => void;

  onGallerySubmit: (imageId: number) => void;
  onImageGalleryModalClose: () => void;
  onAnnouncementRangeModalOpen: () => void;
  onAnnouncementRangeModalClose: () => void;
  onAnnouncementRangeSubmit: (range: AnnouncementRangeDTO) => void;

  onSave: () => Promise<boolean | void>;
}

const AnnouncementEditorContext = createContext<
  AnnouncementEditorContextValue | undefined
>(undefined);

export const useAnnouncementEditorContext = (): AnnouncementEditorContextValue => {
  const context = useContext(AnnouncementEditorContext);
  if (!context) {
    throw new Error(
      'useAnnouncementEditorContext must be used inside AnnouncementEditorProvider',
    );
  }
  return context;
};

interface AnnouncementEditorProviderProps {
  announcementId: number;
  // undefined means a not-yet-saved item — see NEW_ANNOUNCEMENT_ITEM_ID.
  itemId: number | undefined;
  children: React.ReactNode;
}
